import type { Command, CommandResult, EditorSelectionState } from "@/commands/Command";
import { CommandHistory } from "@/commands/CommandHistory";
import { UpdateSelectionCommand } from "@/commands/selectionCommands";
import { TransactionCommand } from "@/commands/transaction";
import {
  DEFAULT_DOCUMENT_VIEWPORT,
  resolveActivePageId,
  type CurvaDocument,
  type DocumentViewport,
} from "@/document/model/document";
import { cloneDocumentForCommand } from "@/document/model/documentClone";
import { expandChangedNodeIdsWithGeometryDrift } from "@/document/model/geometryDrift";
import { selectionEqual } from "@/editor/shell/shellSelection";
import { documentStore, selectionStore, uiStore, viewStore } from "@/editor/store";

export interface RefLike<T> {
  current: T;
}

export interface EditorCommandHostDeps {
  getViewport: () => DocumentViewport;
  onCommitted?: (document: CurvaDocument) => void;
  /** Called when leaving textEdit mode via selection change (commit pending text session). */
  onLeaveTextEdit?: () => void;
}

export class EditorCommandHost {
  constructor(
    private readonly historyRef: RefLike<CommandHistory>,
    private readonly deps: EditorCommandHostDeps,
  ) {}

  get history(): CommandHistory {
    return this.historyRef.current;
  }

  documentWithCurrentViewport = (nextDocument: CurvaDocument): CurvaDocument => {
    const viewport = this.deps.getViewport();
    if (
      nextDocument.viewport?.x === viewport.x
      && nextDocument.viewport?.y === viewport.y
      && nextDocument.viewport?.zoom === viewport.zoom
    ) {
      return nextDocument;
    }

    return {
      ...nextDocument,
      viewport: { ...viewport },
    };
  };

  patchDocument = (updater: (current: CurvaDocument) => CurvaDocument): void => {
    const current = documentStore.getDocument();
    const next = updater(current);
    if (next !== current) {
      documentStore.setDocument(next);
    }
  };

  commit = (
    nextDocument: CurvaDocument,
    nextMessage: string,
    change?: { previousDocument: CurvaDocument; result: CommandResult },
  ): void => {
    const committed = this.documentWithCurrentViewport(nextDocument);
    documentStore.setDocument(committed, change
      ? {
          previousDocument: change.previousDocument,
          // Include auto-layout side effects (nested flex/pattern reflow) so
          // retained-scene incremental updates don't reuse stale leaf geometry.
          changedNodeIds: expandChangedNodeIdsWithGeometryDrift(
            change.previousDocument,
            committed,
            change.result.changedNodeIds,
          ),
          affectedBounds: change.result.affectedBounds,
          previewCompatible: change.result.previewCompatible === true,
        }
      : undefined);
    this.deps.onCommitted?.(documentStore.getDocument());
    uiStore.setMessage(nextMessage);
  };

  currentSelection = (): EditorSelectionState => {
    const document = documentStore.getDocument();
    const selection = selectionStore.getSelection();
    const animation = uiStore.animation.getValue();
    return {
      activePageId: resolveActivePageId(document, selection.activePageId),
      selectedNodeIds: selection.selectedNodeIds,
      primarySelectedNodeId: selection.primarySelectedNodeId,
      mode: selection.mode,
      selectedVertexId: selection.selectedVertexId,
      selectedVertexIds: selection.selectedVertexIds,
      selectedEdgeIds: selection.selectedEdgeIds,
      selectedHandle: selection.selectedHandle,
      selectedAnimationNodeIds: selection.selectedAnimationNodeIds,
      primarySelectedAnimationNodeId: selection.primarySelectedAnimationNodeId,
      selectedAnimationConnectionIds: selection.selectedAnimationConnectionIds,
      animationPanelOpen: animation.panelOpen,
    };
  };

  commandContext = (): { document: CurvaDocument; selection: EditorSelectionState } => {
    return {
      document: cloneDocumentForCommand(this.documentWithCurrentViewport(documentStore.getDocument())),
      selection: this.currentSelection(),
    };
  };

  selectionCommandContext = (): { document: CurvaDocument; selection: EditorSelectionState } => {
    return {
      document: documentStore.getDocument(),
      selection: this.currentSelection(),
    };
  };

  selectionSnapshot = (patch: Partial<EditorSelectionState>): EditorSelectionState => {
    const current = this.currentSelection();
    const nextVertexIds = patch.selectedVertexIds ?? (
      "selectedVertexId" in patch
        ? patch.selectedVertexId ? [patch.selectedVertexId] : []
        : current.selectedVertexIds ?? []
    );
    return {
      ...current,
      ...patch,
      selectedNodeIds: patch.selectedNodeIds ?? current.selectedNodeIds,
      selectedVertexIds: nextVertexIds,
    };
  };

  applySelectionSnapshot = (selection: EditorSelectionState | undefined): void => {
    if (!selection) {
      return;
    }

    selectionStore.setSelection({
      ...selectionStore.getSelection(),
      ...selection,
      selectedVertexIds: selection.selectedVertexIds ?? (selection.selectedVertexId ? [selection.selectedVertexId] : []),
      selectedEdgeIds: selection.selectedEdgeIds ?? [],
      selectedAnimationNodeIds: selection.selectedAnimationNodeIds ?? [],
      selectedAnimationConnectionIds: selection.selectedAnimationConnectionIds ?? [],
    });
    if (selection.animationPanelOpen !== undefined) {
      const playing = uiStore.animation.getValue().playing;
      uiStore.patchAnimation({
        panelOpen: selection.animationPanelOpen,
        playing: selection.animationPanelOpen ? playing : false,
      });
    }
    viewStore.clearVectorPreviews();
    if (selection.mode !== "textEdit") {
      uiStore.setTextSelection(undefined);
    }
  };

  selectionChanged = (next: EditorSelectionState): boolean => {
    return !selectionEqual(this.currentSelection(), next);
  };

  updateSelection = (selection: EditorSelectionState, recordHistory: boolean): void => {
    const current = this.currentSelection();
    if (selectionEqual(current, selection)) {
      return;
    }

    if (current.mode === "textEdit" && selection.mode !== "textEdit") {
      this.deps.onLeaveTextEdit?.();
    }

    if (recordHistory) {
      const context = this.selectionCommandContext();
      this.history.execute(new UpdateSelectionCommand(selection), context);
    }

    this.applySelectionSnapshot(selection);
  };

  runDocumentCommands = (
    commands: Command[],
    label: string,
    options: { afterSelection?: EditorSelectionState } = {},
  ): boolean => {
    if (commands.length === 0) {
      return false;
    }

    const previousDocument = documentStore.getDocument();
    const context = this.commandContext();
    const result = this.history.execute(
      commands.length === 1 ? commands[0]! : new TransactionCommand(label, commands),
      context,
      { afterSelection: options.afterSelection ?? this.currentSelection() },
    );
    this.commit(context.document, label, { previousDocument, result });
    return true;
  };

  undo = (): void => {
    const previousDocument = documentStore.getDocument();
    const context = this.commandContext();
    const result = this.history.undo(context);
    if (result) {
      this.applySelectionSnapshot(context.selection);
      this.commit(context.document, "Undo", { previousDocument, result });
    }
  };

  redo = (): void => {
    const previousDocument = documentStore.getDocument();
    const context = this.commandContext();
    const result = this.history.redo(context);
    if (result) {
      this.applySelectionSnapshot(context.selection);
      this.commit(context.document, "Redo", { previousDocument, result });
    }
  };
}

export function createDefaultCommandHostDeps(
  viewportRef: RefLike<DocumentViewport>,
  documentRef?: RefLike<CurvaDocument>,
  onLeaveTextEdit?: () => void,
): EditorCommandHostDeps {
  return {
    getViewport: () => viewportRef.current,
    onCommitted: documentRef
      ? document => {
          documentRef.current = document;
        }
      : undefined,
    onLeaveTextEdit,
  };
}

export const DEFAULT_COMMAND_VIEWPORT: DocumentViewport = { ...DEFAULT_DOCUMENT_VIEWPORT };
